import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  List, ListItemButton, ListItemIcon, ListItemText, Stack, TextField, Typography,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { number, parseNumber, today, unitShort, weekStart } from '../labels';
import { NeedsHousehold, PageHeader, useUndoable } from '../common';
import { useSheets } from '../tasks';

// ---------- Voice (browser Web Speech API) ----------

const Recognition: any = typeof window !== 'undefined'
  ? (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition
  : undefined;

/** Speech to text in Portuguese, when the browser has it. */
function useSpeech(onText: (text: string) => void, onError: (message: string) => void) {
  const [listening, setListening] = useState(false);
  const recognition = useRef<any>(null);
  const handlers = useRef({ onText, onError });
  handlers.current = { onText, onError };

  useEffect(() => () => recognition.current?.abort?.(), []);

  const start = () => {
    if (!Recognition || listening) return;
    const r = new Recognition();
    r.lang = 'pt-PT';
    r.interimResults = false;
    r.maxAlternatives = 1;
    r.onresult = (event: any) => {
      const text = event.results?.[0]?.[0]?.transcript as string | undefined;
      if (text && text.trim()) handlers.current.onText(text.trim());
    };
    r.onerror = (event: any) => {
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      handlers.current.onError(event.error === 'not-allowed' || event.error === 'service-not-allowed'
        ? 'O browser não deixou usar o microfone. Escreve o comando.'
        : 'Não foi possível ouvir. Escreve o comando.');
    };
    r.onend = () => setListening(false);
    recognition.current = r;
    try {
      r.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  };
  const stop = () => recognition.current?.stop?.();

  return { supported: !!Recognition, listening, start, stop };
}

// ---------- Cards ----------

const EXAMPLES: { title: string; text: string }[] = [
  { title: 'Ementas', text: '«na quinta ao jantar faço…», «troca o jantar de hoje por algo rápido», «sugere as ementas da próxima semana», «amanhã jantamos fora», «quinta ao jantar descongelo a bolonhesa»' },
  { title: 'Sopa', text: '«sopa de legumes no domingo», «fiz sopa», «acabou a sopa»' },
  { title: 'Compras', text: '«acrescenta leite e ovos», «pus queijo no carrinho, 3,49», «não havia bacalhau»' },
  { title: 'Tarefas', text: '«lembra-me de pagar a água na sexta», «a Rita já tirou a loiça», «esta semana o lixo fica com a Rita»' },
  { title: 'Em casa', text: '«tenho 8 ovos», «na despensa tenho 3 latas de feijão, 2 de cogumelos e mais nada», «gastei 2 ovos»,«acabou o azeite», «o arroz está no armário de cima da despensa», «guardei 4 pacotes de arroz na arrecadação», «trouxe 2 da arrecadação», «onde está o arroz?», «quantos ovos tenho?», «quero ter sempre 6 ovos, compro à dúzia», «gasto 1 litro de leite por dia»' },
];

/** The kinds whose target can be a name typed by hand ("Escrever outro…"). */
const CAN_WRITE: T.AssistantActionKind[] = [
  'SetMeal', 'AddToList', 'AddToCart', 'SetStock', 'AdjustStock', 'SetLocation', 'SetMinStock', 'StoreElsewhere', 'BringFromReserve', 'SetDailyUse',
  'CountAt',
];

const withUnit = (label: string, action: T.AssistantAction) => (action.unit ? `${label} (${unitShort(action.unit)})` : label);
const sidesText = (sides: T.AssistantOption[]) => sides.map(s => s.name.toLowerCase()).join(' + ');
const dropWarnings = (warnings: string[], prefixes: string[]) => warnings.filter(w => !prefixes.some(p => w.startsWith(p)));

/** A number typed freely ("3,49"); `onChange` gets null while empty or not a number. */
function NumberField({ label, value, onChange }: { label: string; value?: number | null; onChange: (value: number | null) => void }) {
  const [text, setText] = useState(value === null || value === undefined ? '' : number(value));
  return (
    <TextField
      size="small" fullWidth label={label} value={text} sx={{ mt: 1 }}
      slotProps={{ htmlInput: { inputMode: 'decimal' } }}
      onChange={e => { setText(e.target.value); onChange(parseNumber(e.target.value)); }}
    />
  );
}

/** Ticking side dishes: the dish's options first, then "Sem acompanhamento" or "Feito". */
function SidesDialog({ open, current, options, onClose }: {
  open: boolean; current: T.AssistantOption[]; options: T.AssistantOption[]; onClose: (sides: T.AssistantOption[] | null) => void;
}) {
  const [chosen, setChosen] = useState<T.AssistantOption[]>(current);
  useEffect(() => { if (open) setChosen(current); }, [open, current]);
  const all = options.concat(current.filter(c => !options.some(o => o.publicId === c.publicId)));
  const isChosen = (side: T.AssistantOption) => chosen.some(c => c.publicId === side.publicId);
  const toggle = (side: T.AssistantOption) =>
    setChosen(list => (isChosen(side) ? list.filter(c => c.publicId !== side.publicId) : list.concat(side)));

  return (
    <Dialog open={open} onClose={() => onClose(null)} maxWidth="xs" fullWidth>
      <DialogTitle>Acompanhamentos</DialogTitle>
      {all.length === 0 ? (
        <>
          <DialogContent>
            <DialogContentText>Ainda não há acompanhamentos. Cria receitas do tipo "Acompanhamento" (arroz, batata cozida, salada…).</DialogContentText>
          </DialogContent>
          <DialogActions><Button onClick={() => onClose(null)}>OK</Button></DialogActions>
        </>
      ) : (
        <>
          <List sx={{ pt: 0 }}>
            {all.map(side => (
              <ListItemButton key={side.publicId} onClick={() => toggle(side)} dense>
                <ListItemIcon sx={{ minWidth: 36 }}><Checkbox edge="start" checked={isChosen(side)} tabIndex={-1} disableRipple /></ListItemIcon>
                <ListItemText primary={side.name} secondary={side.detail} />
              </ListItemButton>
            ))}
          </List>
          <DialogActions sx={{ flexWrap: 'wrap' }}>
            <Button onClick={() => onClose(null)}>Cancelar</Button>
            <Button onClick={() => onClose([])}>Sem acompanhamento</Button>
            <Button variant="contained" onClick={() => onClose(chosen)}>Feito</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

/**
 * Voice/text commands: the sentence is read on the server into proposals, shown as editable cards;
 * only the ticked cards are applied, when confirmed.
 */
const AssistantPage: React.FC = () => {
  const navigate = useNavigate();
  const sheets = useSheets();
  const [text, setText] = useState('');
  const [result, setResult] = useState<T.InterpretResult | null>(null);
  const [excluded, setExcluded] = useState<number[]>([]);
  // New proposals reset the number fields (their text lives in them)
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<T.AssistantActionResult[] | null>(null);
  const offerUndo = useUndoable();
  const [sidesFor, setSidesFor] = useState<number | null>(null);

  const interpret = async (value = text) => {
    const command = value.trim();
    if (!command || busy) return;
    setError(null);
    setOutcome(null);
    setBusy(true);
    try {
      setResult(await api.interpret(command));
      setExcluded([]);
      setVersion(v => v + 1);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const speech = useSpeech(heard => { setText(heard); interpret(heard); }, setError);

  const update = (index: number, change: (action: T.AssistantAction) => T.AssistantAction) =>
    setResult(r => (r ? { ...r, actions: r.actions.map((a, i) => (i === index ? change(a) : a)) } : r));

  /** "Mudar": another of the options, or a name typed by hand. */
  const chooseTarget = async (index: number, action: T.AssistantAction, canWrite: boolean) => {
    const options = action.options.map((o, i) => ({ value: String(i), text: o.detail ? `${o.name} (${o.detail})` : o.name, primary: o.publicId === action.targetPublicId }));
    if (canWrite) options.push({ value: 'write', text: 'Escrever outro…', primary: false });
    const choice = await sheets.choose('Escolhe', options);
    if (choice === null) return;
    // The doubts about the old choice no longer apply
    const prefixes = ['Querias dizer', 'Não encontrei', 'Não estava na lista'];
    if (choice === 'write') {
      const typed = await sheets.prompt('Escrever', 'Nome', action.freeText ?? action.targetName ?? '');
      if (!typed) return;
      update(index, a => ({ ...a, targetPublicId: null, targetName: null, freeText: typed, warnings: dropWarnings(a.warnings, prefixes) }));
      return;
    }
    const option = action.options[Number(choice)];
    let sides = action.sides;
    // Another dish: its suggested side
    if (action.kind === 'SetMeal') {
      sides = [];
      try {
        const side = await api.suggestSide(option.publicId);
        if (side) sides = [{ publicId: side.publicId, name: side.name }];
      } catch {
        // No suggestion: no side
      }
    }
    update(index, a => ({ ...a, targetPublicId: option.publicId, targetName: option.name, freeText: null, sides, warnings: dropWarnings(a.warnings, prefixes) }));
  };

  /** Another text: the cards were read from the old one, and confirming would send (and learn from) that one. */
  const changeText = (value: string) => {
    setText(value);
    if (result && value.trim() !== result.text) {
      setResult(null);
      setOutcome(null);
    }
  };

  const confirm = async () => {
    if (!result) return;
    const actions = result.actions.filter((a, i) => a.kind !== 'SuggestWeek' && excluded.indexOf(i) < 0);
    if (actions.length === 0) return;
    setError(null);
    setBusy(true);
    try {
      const results = await api.confirmAssistant({ text: result.text, actions });
      setOutcome(results);
      const done = results.filter(r => r.applied);
      if (done.length === results.length) {
        setResult(null);
        setText('');
      } else if (done.length > 0) {
        // Some went through: their cards go, so confirming again sends only what failed
        const applied = actions.filter((_, i) => results[i].applied);
        const kept = result.actions.map((a, i) => ({ a, i })).filter(({ a }) => applied.indexOf(a) < 0);
        setResult({ ...result, actions: kept.map(({ a }) => a) });
        setExcluded(kept.map(({ i }, n) => (excluded.indexOf(i) >= 0 ? n : -1)).filter(n => n >= 0));
        // The cards move up: new keys, so no field keeps the text of the card that was there
        setVersion(v => v + 1);
      }
      // A way back for what was just done
      const undos = done.map(r => r.undo).filter((u): u is Record<string, unknown> => !!u);
      if (undos.length > 0)
        offerUndo(done.length === 1 ? done[0].title : `${done.length} alterações feitas`,
          () => api.undoAssistant(undos), () => setOutcome(null), { success: 'Desfeito' });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const renderCard = (action: T.AssistantAction, index: number) => {
    const key = `${version}-${index}`;
    const set = (patch: Partial<T.AssistantAction>) => update(index, a => ({ ...a, ...patch }));
    const fields: React.ReactNode[] = [];

    switch (action.kind) {
      case 'SuggestWeek':
        fields.push(
          <Button key="open" variant="contained" sx={{ mt: 1 }}
            onClick={() => navigate(`/casa/refeicoes/sugerir?semana=${weekStart(action.date ?? today())}`)}>
            Abrir sugestões
          </Button>,
        );
        break;
      case 'LinkLeftovers':
      case 'MarkOut':
        break;
      case 'SetAttendees':
        fields.push(<Typography key="members" sx={{ mt: 0.5 }}>{action.memberNames.join(', ')}</Typography>);
        break;
      case 'AddTask':
        fields.push(
          <TextField key="title" size="small" fullWidth placeholder="Tarefa" value={action.freeText ?? ''} sx={{ mt: 1 }}
            onChange={e => set({ freeText: e.target.value })} />,
        );
        if (action.recurrenceText) fields.push(<Typography key="rec" variant="caption" color="text.secondary" component="div">Repete-se {action.recurrenceText}</Typography>);
        if (action.memberName) fields.push(<Typography key="who" variant="caption" color="text.secondary" component="div">Responsável: {action.memberName}</Typography>);
        break;
      default: {
        // "→ Esparguete com camarão  [Mudar]": the recipe, item or task the card acts on
        const target = action.targetName ?? action.freeText;
        const canWrite = CAN_WRITE.indexOf(action.kind) >= 0;
        fields.push(
          <Box key="target" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography sx={{ flexGrow: 1, minWidth: 0, color: target ? undefined : 'text.secondary', overflowWrap: 'anywhere' }}>
              {target ? `→ ${target}` : 'Escolhe uma opção'}
            </Typography>
            {(action.options.length > 0 || canWrite) && <Button size="small" onClick={() => chooseTarget(index, action, canWrite)}>Mudar</Button>}
          </Box>,
        );
      }
    }

    if ((action.kind === 'SetMeal' || action.kind === 'UseFrozen') && action.targetPublicId) {
      fields.push(
        <Box key="sides" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }}>
            {action.sides.length === 0 ? 'Sem acompanhamento' : sidesText(action.sides)}
          </Typography>
          <Button size="small" onClick={() => setSidesFor(index)}>Acompanhamentos</Button>
        </Box>,
      );
    }
    if (action.kind === 'SetMeal' && action.freezeServings !== null && action.freezeServings !== undefined)
      fields.push(<NumberField key={`${key}-freeze`} label="Doses a congelar" value={action.freezeServings} onChange={v => set({ freezeServings: v })} />);
    if (action.kind === 'AddToCart')
      fields.push(<NumberField key={`${key}-price`} label="Preço da prateleira (€)" value={action.price} onChange={v => set({ price: v })} />);
    if (action.kind === 'AddToList')
      fields.push(<NumberField key={`${key}-qty`} label="Quantidade (opcional)" value={action.quantity} onChange={v => set({ quantity: v })} />);
    if (action.kind === 'SetStock')
      fields.push(<NumberField key={`${key}-qty`} label={withUnit('Quanto tens', action)} value={action.quantity} onChange={v => set({ quantity: v })} />);
    if (action.kind === 'AdjustStock') {
      // The sign comes from what was said (gastei / comprei); the field shows the amount
      const sign = action.quantity !== null && action.quantity !== undefined && action.quantity < 0 ? -1 : 1;
      fields.push(
        <NumberField
          key={`${key}-qty`} label={withUnit(sign < 0 ? 'Quanto gastaste' : 'Quanto compraste', action)}
          value={action.quantity === null || action.quantity === undefined ? null : Math.abs(action.quantity)}
          onChange={v => set({ quantity: v === null ? null : v * sign })}
        />,
      );
    }
    if (action.kind === 'SetMinStock') {
      fields.push(<NumberField key={`${key}-min`} label={withUnit('Stock mínimo', action)} value={action.minStock} onChange={v => set({ minStock: v })} />);
      fields.push(<NumberField key={`${key}-reorder`} label="Quanto compras de cada vez" value={action.reorderQuantity} onChange={v => set({ reorderQuantity: v })} />);
    }
    if (action.kind === 'SetDailyUse')
      fields.push(<NumberField key={`${key}-daily`} label={withUnit('Gasto por dia', action)} value={action.quantity} onChange={v => set({ quantity: v })} />);
    if (action.kind === 'StoreElsewhere' || action.kind === 'BringFromReserve')
      fields.push(<NumberField key={`${key}-qty`} label={withUnit('Quanto', action)} value={action.quantity} onChange={v => set({ quantity: v })} />);
    if (action.kind === 'CountAt')
      fields.push(<NumberField key={`${key}-qty`} label={withUnit('Quanto há lá', action)} value={action.quantity} onChange={v => set({ quantity: v })} />);
    if (action.kind === 'SetLocation' || action.kind === 'StoreElsewhere' || action.kind === 'CountAt') {
      fields.push(
        <TextField key="place" size="small" fullWidth placeholder="Local" label="Local" value={action.locationName ?? ''} sx={{ mt: 1 }}
          onChange={e => set({ locationName: e.target.value })} />,
      );
    }
    if (action.kind === 'CompleteTask' && action.memberName)
      fields.push(<Typography key="by" variant="caption" color="text.secondary" component="div">Feita por {action.memberName}</Typography>);

    const included = excluded.indexOf(index) < 0;
    return (
      <Card key={key} variant="outlined" sx={{ p: 1.5, opacity: included || action.kind === 'SuggestWeek' ? 1 : 0.6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }}>{action.title}</Typography>
          {action.kind !== 'SuggestWeek' && (
            <Checkbox
              checked={included} slotProps={{ input: { 'aria-label': 'Incluir' } as any }}
              onChange={e => setExcluded(list => (e.target.checked ? list.filter(i => i !== index) : list.concat(index)))}
            />
          )}
        </Box>
        {fields}
        {action.warnings.map(w => (
          <Typography key={w} variant="body2" sx={{ color: '#E65100', mt: 0.5 }}>⚠ {w}</Typography>
        ))}
      </Card>
    );
  };

  const sidesAction = sidesFor !== null ? result?.actions[sidesFor] : undefined;
  const canConfirm = !!result && result.actions.some(a => a.kind !== 'SuggestWeek');

  return (
    <Box sx={{ maxWidth: 720 }}>
      <PageHeader title="Dizer" subtitle="Escreve ou diz o que queres fazer; confirmas antes de mudar alguma coisa." />
      <Stack spacing={1.5}>
        <TextField
          multiline minRows={2} fullWidth value={text} onChange={e => changeText(e.target.value)}
          placeholder="Ex.: amanhã ao jantar faço bacalhau à brás, sobra para o almoço do dia seguinte"
          onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); interpret(); } }}
        />
        <Stack direction="row" spacing={1}>
          {speech.supported && (
            <Button
              fullWidth variant="outlined" color={speech.listening ? 'error' : 'primary'} disabled={busy}
              startIcon={speech.listening ? <StopIcon /> : <MicIcon />}
              onClick={() => { setError(null); if (speech.listening) speech.stop(); else speech.start(); }}
            >
              {speech.listening ? 'A ouvir…' : 'Falar'}
            </Button>
          )}
          <Button fullWidth variant="contained" onClick={() => interpret()} disabled={busy || !text.trim()}>Interpretar</Button>
        </Stack>

        {!(result && result.understood) && (
          <Box>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ fontWeight: 600 }}>Experimenta:</Typography>
            {EXAMPLES.map(e => (
              <Typography key={e.title} variant="caption" color="text.secondary" component="div">
                <b>{e.title}:</b> {e.text}
              </Typography>
            ))}
          </Box>
        )}

        {busy && <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
        {result?.message && <Alert severity={result.understood ? 'info' : 'warning'}>{result.message}</Alert>}

        {result?.actions.map(renderCard)}

        {canConfirm && (
          <Box>
            <Button variant="contained" size="large" onClick={confirm} disabled={busy}>Confirmar</Button>
          </Box>
        )}
        {outcome && (
          <Box>
            {outcome.map((r, i) => (
              <Typography key={i} sx={{ color: r.applied ? 'success.main' : 'error.main' }}>
                {r.applied ? `✓ ${r.title}` : `✗ ${r.title}: ${r.error ?? ''}`}
              </Typography>
            ))}
          </Box>
        )}
      </Stack>

      <SidesDialog
        open={sidesAction !== undefined}
        current={sidesAction?.sides ?? []}
        options={sidesAction?.sideOptions ?? []}
        onClose={sides => {
          const index = sidesFor;
          setSidesFor(null);
          if (sides === null || index === null) return;
          update(index, a => ({ ...a, sides, warnings: dropWarnings(a.warnings, ['Acompanhamento sugerido']) }));
        }}
      />
      {sheets.element}
    </Box>
  );
};

const AssistantRoute: React.FC = () => <NeedsHousehold><AssistantPage /></NeedsHousehold>;
export default AssistantRoute;
